import { Chip } from '@nextui-org/react'
import { useEffect, useState, useMemo, useRef, useCallback } from 'react'
import { Control, UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { EducationFunctions } from './EducationFunctions'

import { ContainerGroupField } from '@/components/common'
import { LittleText, Text } from '@/components/common/UI'
import {
    AutocompleteControl,
    SelectControl,
    InputControl
} from '@/components/form-controls'
import {
    EducationFormData,
    InstitutionOption
} from '@/types/forms/educationForm'
import { OptionsField } from '@/types/forms/optionsField'

// Special ID for "Other" option
const OTHER_OPTION_ID = 'OTHER_INSTITUTION'

interface UniversityControlProps {
    control: Control<EducationFormData>
    text: string
    watchUniversity: string | null | undefined
    setValue: UseFormSetValue<EducationFormData>
    optionsTittles?: OptionsField[]
    studyStatusOptions: OptionsField[]
    initialName?: string
    // Location props
    items: {
        country?: string
        department?: string
        city?: string
    }
}

interface UniversityOptionRenderProps {
    option: InstitutionOption
}

const UniversityOptionRender = ({
    option
}: UniversityOptionRenderProps): JSX.Element => {
    const { t } = useTranslation()

    return (
        <>
            <p className="text-sm font-medium text-gray-900 truncate">
                {t(option.label)}
            </p>
            {(option.city || option.country || option.department) && (
                <div className="flex gap-1 mt-1 flex-wrap">
                    {option.country && (
                        <Chip
                            variant="bordered"
                            size="sm"
                        >
                            {option.country}
                        </Chip>
                    )}
                    {option.department && (
                        <Chip
                            color="primary"
                            size="sm"
                        >
                            {option.department}
                        </Chip>
                    )}
                    {option.city && (
                        <Chip
                            color="secondary"
                            size="sm"
                        >
                            {option.city}
                        </Chip>
                    )}
                </div>
            )}
        </>
    )
}

interface UniversityInfoProps {
    selectedUniversity: InstitutionOption
    newText: string
    text: string
}

const UniversityInfo = ({
    selectedUniversity,
    newText,
    text
}: UniversityInfoProps): JSX.Element => {
    const { t } = useTranslation()

    return (
        <article className="p-2 space-y-2">
            <div className="flex items-center space-x-2">
                <Text text={t(`${newText}.universityCountry`)} />
                <LittleText
                    text={
                        selectedUniversity?.country || t(`${text}.noAvailable`)
                    }
                />
            </div>
            <div className="flex items-center space-x-2">
                <Text text={t(`${newText}.universityDepartment`)} />
                <LittleText
                    text={
                        selectedUniversity?.department ||
                        t(`${text}.noAvailable`)
                    }
                />
            </div>
            <div className="flex items-center space-x-2">
                <Text text={t(`${newText}.universityCity`)} />
                <LittleText
                    text={selectedUniversity?.city || t(`${text}.noAvailable`)}
                />
            </div>
        </article>
    )
}

// Custom hook to handle university selection logic
const useUniversitySelection = (
    initialName: string | undefined,
    watchUniversity: string | null | undefined,
    items: { country?: string; department?: string; city?: string },
    getInstitutions: (search: string, isSchool: boolean) => Promise<any>,
    t: (key: string) => string
) => {
    const [searchUniversityName, setSearchUniversityName] = useState('')

    // Create "Other" option first (needed for initial state)
    const otherOption: InstitutionOption = useMemo(
        () => ({
            value: OTHER_OPTION_ID,
            label: t('educationInformation.otherInstitution'),
            code: OTHER_OPTION_ID,
            country: '',
            department: '',
            city: ''
        }),
        [t]
    )

    const initialOption: InstitutionOption | null = useMemo(() => {
        if (initialName && watchUniversity) {
            // Si el watchUniversity ya es "OTHER_INSTITUTION", devolver la opción "Otro"
            if (watchUniversity === OTHER_OPTION_ID) {
                return otherOption
            }

            return {
                label: initialName,
                value: watchUniversity,
                code: watchUniversity,
                country: items.country || '',
                department: items.department || '',
                city: items.city || ''
            }
        }

        return null
    }, [initialName, watchUniversity, items, otherOption])

    const [optionsUniversities, setOptionsUniversities] = useState<
        InstitutionOption[]
    >(initialOption ? [initialOption] : [])
    const [selectedUniversity, setSelectedUniversity] =
        useState<InstitutionOption | null>(initialOption)

    // Update selectedUniversity when initialOption changes (preloaded data)
    useEffect(() => {
        if (initialOption) {
            setSelectedUniversity(initialOption)
        }
    }, [initialOption])

    // Sync selectedUniversity when watchUniversity changes
    useEffect(() => {
        if (watchUniversity && !selectedUniversity) {
            const found = optionsUniversities.find(
                (opt) => String(opt.value) === String(watchUniversity)
            )

            if (found) {
                setSelectedUniversity(found)
            }
        }
    }, [watchUniversity, selectedUniversity, optionsUniversities])

    // Helper function to update options when search is less than 6 characters
    const updateOptionsForShortSearch = useCallback(() => {
        setOptionsUniversities((prev) => {
            if (initialOption) {
                const current = prev.find(
                    (opt) => opt.value === initialOption.value
                )

                if (current) {
                    return [current]
                }

                return [initialOption]
            }

            return []
        })
    }, [initialOption])

    // Helper function to fetch universities from API
    const fetchUniversities = useCallback(async (): Promise<void> => {
        const universities = await getInstitutions(searchUniversityName, false)

        if (universities && universities.length > 0) {
            setOptionsUniversities([...universities, otherOption])
        } else {
            setOptionsUniversities([otherOption])
        }
    }, [searchUniversityName, otherOption, getInstitutions])

    useEffect(() => {
        if (searchUniversityName.length >= 6) {
            fetchUniversities()
        } else {
            updateOptionsForShortSearch()
        }
    }, [searchUniversityName, fetchUniversities, updateOptionsForShortSearch])

    return {
        searchUniversityName,
        setSearchUniversityName,
        optionsUniversities,
        selectedUniversity,
        setSelectedUniversity
    }
}

// Sub-component for University Autocomplete to reduce main component size
interface UniversityAutocompleteProps {
    control: Control<EducationFormData>
    options: InstitutionOption[]
    searchName: string
    setSearch: (value: string) => void
    t: (key: string) => string
    text: string
    newText: string
    onSelectionChange: (selected: InstitutionOption | null) => void
    setValue: UseFormSetValue<EducationFormData>
}

const UniversityAutocomplete = ({
    control,
    options,
    searchName,
    setSearch,
    t,
    text,
    newText,
    onSelectionChange,
    setValue
}: UniversityAutocompleteProps) => (
    <div className="flex flex-col gap-2">
        <AutocompleteControl
            name="universityId"
            control={control}
            label={t(`${newText}.universityName`)}
            options={options}
            rules={{ required: t(`${text}.required.institutionName`) }}
            disableLocalFilter={true}
            popoverProps={{
                offset: 4,
                placement: 'bottom'
            }}
            listboxProps={{
                emptyContent: (
                    <div className="px-3 py-2 text-sm text-gray-500 italic">
                        {searchName.length < 6
                            ? 'Escribe al menos 6 letras para buscar '
                            : 'No se encuentran universidades'}
                    </div>
                ),
                topContent:
                    searchName.length >= 6 &&
                    options.length === 1 &&
                    options[0].value === OTHER_OPTION_ID ? (
                        <div className="px-3 py-2 text-sm text-gray-500 bg-gray-50 border-b">
                            <p className="font-medium text-gray-700">
                                No se encuentran universidades
                            </p>
                            <p className="text-xs mt-1">
                                Puedes seleccionar la opción "Otro" para
                                escribir manualmente
                            </p>
                        </div>
                    ) : null,
                bottomContent:
                    options.length > 1 && options.length === 51 ? (
                        <div className="px-3 py-2 text-xs text-blue-600 bg-blue-50 border-t">
                            Mostrando primeros 50 resultados. Sé más específico
                            para refinar la búsqueda.
                        </div>
                    ) : null
            }}
            customRender={(option) => (
                <UniversityOptionRender option={option} />
            )}
            onInputChange={setSearch}
            onChangeCustom={(value) => {
                const selected = options.find(
                    (opt) => String(opt.value) === String(value)
                )

                if (selected) {
                    // Si selecciona "Otro", limpiar datos de ubicación
                    if (selected.value === OTHER_OPTION_ID) {
                        setValue('universityName', '')
                        setValue('universityCountry', '')
                        setValue('universityDepartment', '')
                        setValue('universityMunicipality', '')
                    } else {
                        // Guardar el nombre y ubicación en el formulario
                        setValue('universityName', selected.label)
                        setValue('universityCountry', selected.country)
                        setValue('universityDepartment', selected.department)
                        setValue('universityMunicipality', selected.city)
                    }
                }

                onSelectionChange(selected || null)
            }}
        />
    </div>
)

export const UniversityControl = ({
    control,
    text,
    watchUniversity,
    setValue,
    optionsTittles,
    studyStatusOptions,
    initialName,
    items
}: UniversityControlProps): JSX.Element => {
    const { t } = useTranslation()
    const { getInstitutions } = EducationFunctions()
    const hasInitialized = useRef(false)

    const {
        searchUniversityName,
        setSearchUniversityName,
        optionsUniversities,
        selectedUniversity,
        setSelectedUniversity
    } = useUniversitySelection(
        initialName,
        watchUniversity,
        items,
        getInstitutions,
        t
    )

    const newText = `${text}.university`
    const isOtherSelected = watchUniversity === OTHER_OPTION_ID

    // Effect to handle legacy data (nombre sin código) - auto-seleccionar "Otro"
    useEffect(() => {
        // Solo para datos legacy: si hay nombre pero no hay código, seleccionar "Otro"
        if (
            initialName &&
            (!watchUniversity || watchUniversity === '') &&
            !hasInitialized.current
        ) {
            setValue('universityId', OTHER_OPTION_ID, { shouldValidate: false })
            setValue('customUniversityName', initialName, {
                shouldValidate: false
            })
            hasInitialized.current = true
        } else if (initialName && watchUniversity) {
            hasInitialized.current = true
        }
    }, [initialName, watchUniversity, setValue])

    // Clear custom name when switching away from "Other" - pero solo después de inicializar
    useEffect(() => {
        // Solo limpiar si ya se inicializó y el usuario cambió de opción
        if (
            hasInitialized.current &&
            !isOtherSelected &&
            watchUniversity &&
            watchUniversity !== OTHER_OPTION_ID
        ) {
            setValue('customUniversityName', undefined)
        }
    }, [isOtherSelected, watchUniversity, setValue])

    return (
        <ContainerGroupField tittleGroup={t('forms.groups.university')}>
            <UniversityAutocomplete
                control={control}
                options={optionsUniversities}
                searchName={searchUniversityName}
                setSearch={setSearchUniversityName}
                t={t}
                text={text}
                newText={newText}
                onSelectionChange={setSelectedUniversity}
                setValue={setValue}
            />

            {/* Custom input field when "Other" is selected */}
            {isOtherSelected && (
                <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                    <InputControl
                        name="customUniversityName"
                        control={control}
                        label={t('educationInformation.customUniversityName')}
                        placeholder={t(
                            'educationInformation.customUniversityPlaceholder'
                        )}
                        rules={{
                            required: t(`${text}.required.institutionName`)
                        }}
                        description={t(
                            'educationInformation.customInstitutionDescription'
                        )}
                        maxWords={200}
                        capitalize={true}
                        onChangeCustom={(value) => {
                            // Store custom name as university name
                            setValue('universityName', value)
                        }}
                    />
                </div>
            )}

            {watchUniversity && selectedUniversity && !isOtherSelected && (
                <UniversityInfo
                    selectedUniversity={selectedUniversity}
                    newText={newText}
                    text={text}
                />
            )}

            <AutocompleteControl
                name="universityTitle"
                control={control}
                label={t(`${text}.tittle`)}
                options={optionsTittles || []}
                rules={{
                    required: t(`${text}.required.tittle`)
                }}
            />

            <SelectControl
                name="universityStudyStatus"
                control={control}
                label={t('educationInformation.homologate.statusEducation')}
                options={studyStatusOptions}
                rules={{
                    required: t(
                        'educationInformation.homologate.required.statusEducation'
                    )
                }}
            />
        </ContainerGroupField>
    )
}
